import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useMemo } from 'react';
import { usePlatformData } from '../../hooks/usePlatformData';
import type { ApiEndpoints } from '@avisoma/shared';
import type { UseQueryResult } from '@tanstack/react-query';

interface MissingCategoriesAlertProps {
  supertypesQuery: UseQueryResult<ApiEndpoints<Date>['/api/supertypes']['GET']['response']>;
}

export function MissingCategoriesAlert({ supertypesQuery }: MissingCategoriesAlertProps) {
  const platformData = usePlatformData();

  const missingCategories = useMemo(() => {
    if (!supertypesQuery.data?.items || !platformData.categories.data) return [];
    const data = platformData.categories.data;
    const allSupertypeCategories = supertypesQuery.data.items.flatMap((item) => item.specs.categories.map((c) => c[0]));
    const missing = allSupertypeCategories.filter((category) => !data.pageCategories.includes(category));
    return Array.from(new Set(missing));
  }, [platformData.categories.data, supertypesQuery.data]);

  if (missingCategories.length === 0) return null;

  return (
    <div className="mb-6">
      <div className="rounded-lg bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-900 p-4 flex items-start gap-4 shadow-sm">
        <div className="flex-shrink-0 mt-1">
          <ExclamationTriangleIcon className="size-6 text-yellow-500 dark:text-yellow-400" />
        </div>
        <div>
          <div className="font-semibold text-yellow-800 dark:text-yellow-200 text-lg mb-1 flex items-center gap-2">
            Warning: Missing Categories
          </div>
          <div className="text-yellow-800 dark:text-yellow-200 mb-2">
            The following categories are referenced in supertypes but do not exist in your platform's category list.
            Please review and add them if needed.
          </div>
          <ul className="list-disc list-inside text-yellow-900 dark:text-yellow-200 font-medium">
            {missingCategories.map((category) => (
              <li key={category}>{category}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
